import { tWithKeyAttr as t } from "../../../i18nHelper";
import React, { useEffect, useRef, useState } from "react";
import styles from "./barchart.module.css";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TOptionData, TQuestionData } from "../../../services/types/user.types";
import { ColorPalette } from "../../../utils/constants";
import ReactDOM from "react-dom";
import useMediaQuery from "../../../hooks/useMediaQuery";



interface BarChartProps {
    data: TQuestionData;
    total: number;
    isPopup?: boolean;
}

const BarChartComponent: React.FC<BarChartProps> = ({ data, total, isPopup }) => {
    const mobile = useMediaQuery("(max-width: 900px)");

    const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
    const [hoveredLegend, setHoveredLegend] = useState<number | null>(null);
    const spanRefs = useRef<(HTMLSpanElement | null)[]>([]);

    const [chartData, setChartData] = useState([{ name: "", value: -1, color: "#fff" }]);
    const [isEmpty, setIsEmpty] = useState<boolean>(false);

    const getColor = (index: number) => {
        const usedColors: string[] = chartData.map(i => i.color);
        if (index < ColorPalette.length) {
            const color = ColorPalette[index];
            usedColors.push(color);
            return color;
        }

        let color = "#" + Math.floor(Math.random() * 16777215).toString(16) + ""
        usedColors.push(color);
        return color;
    };

    const totalBars = chartData.filter(op => op.value > 0).length;
    const calculatedBarSize = isPopup ?
        (totalBars > 25 ? 10 : totalBars > 15
            ? 20
            : totalBars > 10
                ? 30
                : totalBars > 5
                    ? 40
                    : 40)
        : (totalBars > 15
            ? 6
            : totalBars > 10
                ? 10
                : totalBars > 5
                    ? 20
                    : 35);

    const renderCustomLegend = () => {
        return chartData.map((entry, index) => (
            <div
                key={index}
                className={styles.colorItem}
                style={{ position: "relative", display: "flex", alignItems: "center" }}
                onMouseEnter={() => {
                    const el = spanRefs.current[index];
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        setTooltipPos({
                            top: rect.bottom + 5,
                            left: rect.left,
                        });
                    }
                    setHoveredLegend(index);
                }}
                onMouseLeave={() => {
                    setHoveredLegend(null);
                    setTooltipPos(null);
                }}
            >
                <div
                    className={styles.colorBox}
                    style={{ backgroundColor: entry.color }}
                />
                <span
                    ref={(el) => (spanRefs.current[index] = el)}
                    className={styles.legendText}
                    style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "160px",
                        display: "inline-block",
                    }}
                >
                    {entry.name}
                </span>
            </div>
        ));
    };


    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const percentage = Math.round(payload[0].value);

            return (
                <div className="custom-tooltip">
                    <p className="label" style={{ color: 'white', fontSize: '15px' }}>
                        {percentage}%
                    </p>
                </div>
            );
        }
        return null;
    };

    useEffect(() => {
        setChartData(data.options.map((option: TOptionData, index) => ({
            name: option.desc,
            value: option.answersCount,
            color: getColor(index)
        })))

        if (!data.options.find(op => op.answersCount > 0))
            setIsEmpty(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
        <div className={styles.chartContainer}>
            <div className={styles.colorMap}>
                {renderCustomLegend()}
            </div>
            <ResponsiveContainer width={isPopup ? "100%" : 300} height={isPopup ? "180%" : "120%"}>
                {
                    !isEmpty ?
                        (<BarChart data={chartData.filter(cell => cell.value !== 0)} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} height={65}>
                            <XAxis dataKey="name" tick={false} />
                            <YAxis
                                tickFormatter={(value) => `${value}`}
                                domain={[0, Math.ceil(Math.max(...chartData.map(cell => Math.ceil(cell.value))) * 1.2)]}
                                tickMargin={30}
                                fontSize={'14px'}
                                fontWeight={'bold'}
                                allowDecimals={false}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="value" barSize={isPopup ? calculatedBarSize : 50}>
                                {chartData.filter(cell => cell.value !== 0).map((entry, index) => (
                                    <Cell width={(mobile && isPopup) ? calculatedBarSize / 2 : calculatedBarSize} key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>)
                        : <h4 className={styles.chart_message}>{t('NO_DATA_TO_DISPLAY')}</h4>}
            </ResponsiveContainer>
            {
                hoveredLegend !== null &&
                tooltipPos &&
                ReactDOM.createPortal(
                    <div
                        className={styles.legendTooltip}
                        style={{
                            position: "absolute",
                            top: tooltipPos.top,
                            left: tooltipPos.left,
                        }}
                    >
                        {chartData[hoveredLegend ?? 0]?.name ?? t('UNKNOWN')}
                    </div>,
                    document.body
                )}

        </div>
    );
};

export default BarChartComponent;
